import * as React from 'react';
import { useState } from 'react';
import {
  countWords,
  getLongestWord,
  getWords,
  reverseObject,
  TextsType,
  wordsToString
} from '../../truncateHelper';
import { fitStrings } from './fitStrings';
import { getAvailableWidth } from './getAvailableWidth';
import { generateCacheKey } from './generateCacheKey';
import { getWidthOfText } from './getWidthOfText';
import { getStyles } from './getStyles';

interface InterfaceUseTruncateProps {
  str: string;
  fontsLoaded?: boolean;
  widthLimit?: number;
  lines: number;
  suffixComponentRef?: React.RefObject<HTMLSpanElement>;
  highlightedText?: string;
  cachePrefix?: string;
}

const cachedTruncated = {};

export const useTruncateText = (props: InterfaceUseTruncateProps) => {
  const [stringsArray, setstringsArray] = React.useState<string[]>([]);
  //const stringsArray = React.useRef<string[]>([]);
  const containerRef = React.useRef<HTMLDivElement>(null);

  const {
    str,
    fontsLoaded,
    widthLimit,
    lines,
    suffixComponentRef,
    highlightedText,
    cachePrefix
  } = props;

  /** truncate a text to display on a single line */
  const truncateText = React.useCallback(
    (fullStr: string, maxWidth: number, font, letterSpacing) => {
      const ellipsis = '…';
      const textWidth = getWidthOfText(fullStr, font, letterSpacing);

      if (textWidth > maxWidth) {
        const middleIndex = Math.floor(fullStr.length / 2);

        let frontStr = fullStr.slice(0, middleIndex);
        let backStr = fullStr.slice(middleIndex);
        let newStr = frontStr + ellipsis + backStr;
        if (getWidthOfText(newStr, font, letterSpacing) >= maxWidth) {
          for (
            let i = 1;
            i < middleIndex &&
            getWidthOfText(newStr, font, letterSpacing) >= maxWidth;
            i++
          ) {
            frontStr = fullStr.slice(0, middleIndex - i);
            backStr = fullStr.slice(middleIndex + i);
            newStr = frontStr + ellipsis + backStr;
          }
        }
        return newStr;
      }
      return fullStr;
    },
    []
  );

  /**HIGHLIGHT */
  /** truncate a text to display in the highlight */
  const truncateTextHighlight = React.useCallback(
    (
      fullStr: string,
      maxWidth: number,
      font,
      letterSpacing,
      delimiters = [' ', '_', '.']
    ) => {
      const textWidth = getWidthOfText(fullStr, font, letterSpacing);
      /**
       * check if the highlighted text is in string
       * does the highlighted text fit the container
       * if it does we want to add more context to it
       */
      if (textWidth < maxWidth) {
        return fullStr;
      } else {
        const ellipsis = '…';

        const highlightStr = highlightedText;
        const highlightExp = new RegExp(`(${highlightStr})`, 'i');

        // prevent parts[i] is `undefined` issue
        const parts = ['', '', ''].map((p, idx) => {
          const splitted = fullStr?.split(highlightExp);
          return splitted[idx] || p;
        });

        if (!parts) {
          return '';
        }
        if (parts?.length != 3) {
          console.error('More than three parts, need to be fiexed');
        }

        if (parts[1]) {
          // highlight expression is matched

          if (getWidthOfText(parts[1], font, letterSpacing) > maxWidth) {
            // we can ignore part 0 and 2, because even just the highlighted text won't fit
            // we should remove letters from the end of the string
            return fitStrings({
              fromEnd: parts[1],
              maxWidth,
              ellipsis,
              getWidthOfText: (text) =>
                getWidthOfText(text, font, letterSpacing),
              delimiters
            });
          } else {
            return fitStrings({
              fromStart: parts[0],
              fromEnd: parts[2],
              required: parts[1],
              maxWidth,
              ellipsis,
              getWidthOfText: (text) =>
                getWidthOfText(text, font, letterSpacing),
              delimiters
            });
          }
        } else {
          // nothing to highlight (when would this happen?)
          // we should remove letters from the end of the string
          return fitStrings({
            fromEnd: parts[0],
            maxWidth,
            ellipsis,
            getWidthOfText: (text) => getWidthOfText(text, font, letterSpacing),
            delimiters
          });
        }
      }
    },
    [highlightedText]
  );

  /** truncate a text to display on multiple lines
   * - convert full string to an array of strings where each line fits into the container
   * - word-break: break-word
   */
  const truncateTextMultiple = React.useCallback(
    (words: string[], maxWidth: number, font, letterSpacing) => {
      const ellipsis = '…';

      /* - truncate full string to display on multiple lines:
       * the first row: add words from start -> end
       * the remaining rows: add words from end -> start
       */
      const firstRow: string[] = [];
      /** store the remaining rows in reverse order  */
      const remainingRows: TextsType = {};

      for (let row = 0; row < lines; row++) {
        let startIndex = countWords(remainingRows);
        const newRow = [];

        // generate the first row
        if (row === 0) {
          for (let w = 0; w < words.length; w++) {
            words[w] && firstRow.push(words[w]);
            // break if the newRow width > maxWidth
            if (
              getWidthOfText(wordsToString(firstRow), font, letterSpacing) >
              maxWidth
            ) {
              do {
                firstRow.pop();
              } while (
                getWidthOfText(wordsToString(firstRow), font, letterSpacing) >
                maxWidth
              );
              break;
            }
          }
        } else {
          // generate the remaining rows
          const restWords = words.slice(firstRow.length).reverse();
          if (restWords.length === 0) break;

          for (let w = 0; w < restWords.length - startIndex; w++) {
            const word = restWords[w + startIndex];
            word && newRow.push(word);
            if (
              getWidthOfText(wordsToString(newRow), font, letterSpacing) >
              maxWidth
            ) {
              do {
                newRow.pop();
                startIndex = Math.max(0, startIndex - 1);
              } while (
                getWidthOfText(wordsToString(newRow), font, letterSpacing) >
                maxWidth
              );
              remainingRows[row] = newRow.reverse();
              break;
            }

            // if the loop is done and `newRow`'s width < maxWidth, should save the `newRow`
            if (w + startIndex === restWords.length - 1 && newRow.length > 0) {
              remainingRows[row] = newRow.reverse();
              break;
            }
          }
        }
      }

      const remainingRowsLength = Object.keys(remainingRows).length;
      const combined = {
        ...remainingRows,
        [remainingRowsLength + 1]: firstRow
      };
      const allRows = reverseObject(combined);

      /** covert the texts object to an array of strings
       * - add ellipsis if
       *    - it is the last row
       *    - the fullStr has been truncated or the last row width > container width
       */
      const stringsArray = Object.keys(allRows).reduce(
        (acc: string[], k: string) => {
          if (
            !!allRows[Number(k) - 1] &&
            !allRows[Number(k) + 1] &&
            (countWords(allRows) < words.length ||
              getWidthOfText(
                wordsToString(allRows[k]) + ellipsis,
                font,
                letterSpacing
              ) > maxWidth)
          ) {
            do {
              allRows[k] = allRows[k].slice(1);
            } while (
              getWidthOfText(
                wordsToString(allRows[k]) + ellipsis,
                font,
                letterSpacing
              ) > maxWidth
            );

            return [...acc, ellipsis + wordsToString(allRows[k])];
          }
          return [...acc, wordsToString(allRows[k])];
        },
        []
      );
      //console.log('all lines:', stringsArray);

      return stringsArray;
    },
    [lines]
  );

  const [, startTransition] = React.useTransition();

  // try to detect containerWidths when component rerenders
  const [containerWidth, setContainerWidth] = useState(null);

  React.useLayoutEffect(() => {
    const widthOfWidestLetter = containerRef.current
      ? getWidthOfText(
          'W',
          getStyles(containerRef.current).font,
          getStyles(containerRef.current).letterSpacing
        )
      : 0;

    const isSingleWord = str && str.split(' ').length === 1;

    const fullStr = str ? str.toString().trim() : '';
    const style = getStyles(containerRef.current);
    /** if the end-of-line space is too small for a letter, the letter wraps to the next line, this width should be in the calculation */
    const maxWidth = getAvailableWidth(
      containerRef.current,
      style.padding,
      isSingleWord ? 0 : widthOfWidestLetter,
      suffixComponentRef?.current,
      widthLimit
    );

    const longestWord = getLongestWord(fullStr);
    let truncated: string[];

    // used for search
    if (highlightedText) {
      truncated = [
        truncateTextHighlight(
          fullStr,
          maxWidth,
          style.font,
          style.letterSpacing
        )
      ];
    }
    // single line truncate
    else if (lines === 1) {
      truncated = [
        truncateText(fullStr, maxWidth, style.font, style.letterSpacing)
      ];
    }
    // edge case to test char by char if there are unbreakable words
    else if (
      getWidthOfText(longestWord, style.font, style.letterSpacing) > maxWidth
    ) {
      truncated = truncateTextMultiple(
        fullStr.split(''),
        maxWidth,
        style.font,
        style.letterSpacing
      );
    }
    // multi line truncate
    else {
      truncated = truncateTextMultiple(
        getWords(fullStr),
        maxWidth,
        style.font,
        style.letterSpacing
      );
    }

    setstringsArray(truncated);
  }, [containerWidth, str, fontsLoaded]);

  // observe the container's size and trigger updates when its size changes
  React.useLayoutEffect(() => {
    const element = containerRef.current;
    if (!element) return;

    const resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const newWidth = entry.target.clientWidth;
        startTransition(() => setContainerWidth(newWidth));
      }
    });
    startTransition(() => setContainerWidth(element.clientWidth));

    resizeObserver.observe(element);
    return () => {
      resizeObserver.unobserve(element);
      resizeObserver.disconnect();
    };
  }, []);

  const cacheKey =
    cachePrefix && generateCacheKey(`${cachePrefix}-${containerWidth}`, str);
  React.useEffect(() => {
    // store last truncated value before unmount
    return () => {
      if (str && cacheKey && stringsArray.length) {
        cachedTruncated[cacheKey] = stringsArray;
      }
    };
  }, [stringsArray]);

  return {
    containerRef,
    stringsArray:
      !stringsArray.length && cacheKey && cachedTruncated[cacheKey]
        ? cachedTruncated[cacheKey]
        : stringsArray
  };
};

export default useTruncateText;
